import React, { useState, useRef, useCallback } from 'react';
import { api } from '../contexts/AuthContext';
import { TERMS_VERSION } from '../config/terms';

/**
 * Terms & Conditions page
 * - Scrollable T&C document with progress indicator
 * - Accept button disabled until user scrolls to bottom
 * - Two required checkboxes
 * - Calls POST /api/auth/accept-terms on acceptance
 */
export default function TermsAndConditions({ onAccepted, standalone }) {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [reachedBottom, setReachedBottom] = useState(false);
  const [checkedTc, setCheckedTc] = useState(false);
  const [checkedAge, setCheckedAge] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const contentRef = useRef(null);

  const handleScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight - el.clientHeight;
    if (scrollHeight <= 0) {
      setScrollProgress(100);
      setReachedBottom(true);
      return;
    }
    const progress = Math.round((scrollTop / scrollHeight) * 100);
    setScrollProgress(progress);
    if (progress >= 95) {
      setReachedBottom(true);
    }
  }, []);

  const canAccept = reachedBottom && checkedTc && checkedAge;

  const handleAccept = async () => {
    if (!canAccept) return;
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/accept-terms', { version: TERMS_VERSION });
      if (onAccepted) onAccepted();
      else if (standalone) window.location.href = '/';
    } catch (err) {
      setError('Failed to record your acceptance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = () => {
    window.location.href = '/terms-declined';
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Terms and Conditions</h2>
          <p style={styles.subtitle}>Please read carefully and scroll to the bottom to accept.</p>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: scrollProgress + '%' }} />
          </div>
          <span style={styles.progressLabel}>{scrollProgress}% read</span>
        </div>

        <div
          ref={contentRef}
          onScroll={handleScroll}
          style={styles.content}
        >
          <h3>WINESUP TERMS AND CONDITIONS</h3>
          <p><strong>Effective Date: March 14, 2026</strong></p>

          <h4>1. ACCEPTANCE OF TERMS</h4>
          <p>By accessing or using Winesup (the "Service"), you agree to be bound by these Terms and Conditions. If you do not agree, you may not use the Service. These Terms constitute a legally binding agreement between you and Winesup.</p>

          <h4>2. ELIGIBILITY AND AGE RESTRICTION — ALCOHOL CONTENT</h4>
          <p>The Winesup service is exclusively intended for adults of legal drinking age. By using this service, you confirm that:</p>
          <ul>
            <li>You are of legal drinking age in your country of residence (18 years or older in most countries; 21 years or older in the United States).</li>
            <li>You accept full responsibility for ensuring that minors in your household do not access this service.</li>
          </ul>
          <p>Winesup reserves the right to terminate accounts where there is reasonable suspicion of use by minors. We strongly encourage responsible drinking and do not promote excessive alcohol consumption.</p>

          <h4>3. PERMITTED USE</h4>
          <p>You may use Winesup solely for personal, non-commercial purposes related to wine discovery, food and wine pairing, and culinary recommendations. You may NOT use Winesup for:</p>
          <ul>
            <li>Attempting to use the AI assistant for topics outside wine, food, and culinary experiences.</li>
            <li>Data scraping, automated access, or mass collection of information.</li>
            <li>Any illegal purposes or activities.</li>
            <li>Reverse engineering, decompiling, or attempting to access source code.</li>
            <li>Impersonating other users or Winesup staff.</li>
          </ul>

          <h4>4. AI ASSISTANT USAGE AND LIMITATIONS</h4>
          <p>The Winesup AI assistant ("Vinha") is exclusively scoped to wine, food, and culinary topics. Attempting to use the AI for other purposes is a violation of these Terms.</p>
          <ul>
            <li>Off-topic use is actively monitored and logged.</li>
            <li>Repeated attempts to use the service outside its intended purpose may result in account suspension without refund.</li>
            <li>Recommendations provided by Vinha are informational only and do not constitute professional advice.</li>
            <li>Winesup is not liable for any decisions made based on AI recommendations.</li>
          </ul>

          <h4>5. MONITORING AND ENFORCEMENT</h4>
          <p>To ensure compliance with these Terms:</p>
          <ul>
            <li>Conversations with the AI assistant are monitored for compliance with permitted use policies.</li>
            <li>Off-topic conversations are logged and may be reviewed by Winesup staff.</li>
            <li>Winesup reserves the right to terminate accounts without prior notice for violations.</li>
            <li>No refund will be issued upon termination due to Terms violations.</li>
          </ul>

          <h4>6. ACCOUNT TERMINATION</h4>
          <p>Winesup may suspend or terminate your account for:</p>
          <ul>
            <li>Violation of any provision of these Terms.</li>
            <li>Misuse of the AI assistant outside its intended purpose.</li>
            <li>Suspected access by minors.</li>
            <li>Any conduct deemed harmful to Winesup or its users.</li>
          </ul>
          <p>Upon termination, your data may be retained for legal compliance purposes as required by applicable law.</p>

          <h4>7. PRIVACY AND DATA</h4>
          <p>Your personal data and conversation history are private and accessible only to you and authorized Winesup administrators for safety and compliance purposes. Winesup does not sell your personal data to third parties. By using the Service, you consent to the collection and use of data as described in our Privacy Policy.</p>

          <h4>8. DISCLAIMER OF WARRANTIES</h4>
          <p>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WINESUP DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF HARMFUL COMPONENTS.</p>

          <h4>9. LIMITATION OF LIABILITY</h4>
          <p>TO THE FULLEST EXTENT PERMITTED BY LAW, WINESUP SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE, EVEN IF WINESUP HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. IN NO EVENT SHALL WINESUP'S TOTAL LIABILITY EXCEED THE AMOUNT PAID BY YOU FOR THE SERVICE IN THE PRECEDING 12 MONTHS.</p>

          <h4>10. CHANGES TO TERMS</h4>
          <p>Winesup reserves the right to modify these Terms and Conditions at any time, without prior notice. The most current version of these Terms will always be available within the Service.</p>
          <p>By maintaining an active Winesup account, you agree to be bound by the current version of these Terms at all times. Your continued use of the Service following any modification constitutes your acceptance of the revised Terms.</p>
          <p>While Winesup will make reasonable efforts to notify registered users of material changes via the email address associated with their account, Winesup is not responsible for notifications that are misrouted, undelivered, filtered as spam, or otherwise not received. It is your responsibility to review these Terms periodically. If you do not agree with a modification, your sole remedy is to discontinue use of the Service and close your account.</p>

          <h4>11. GOVERNING LAW</h4>
          <p>These Terms shall be governed by and construed in accordance with the laws of the Republic of Costa Rica, without regard to its conflict of law provisions. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of Costa Rica.</p>

          <h4>12. CONTACT</h4>
          <p>For questions about these Terms, please contact us at: <a href="mailto:support@winesup.org">support@winesup.org</a></p>

          <h4>13. SUBSCRIPTION TIERS AND SERVICE LIMITS</h4>
          <p>Winesup offers the following service tiers:</p>
          <p><strong>Free Trial.</strong> New accounts are granted a 7-day free trial with access to all Premium features. At the end of the trial period, the account automatically transitions to the Basic tier unless a paid subscription is activated. No payment information is required to use the free trial.</p>
          <p><strong>Basic.</strong> Provides access to the Winesup AI sommelier at Simple and Balanced complexity levels, with a limit of six (6) menu scans per calendar month. The scan counter resets on the first day of each calendar month.</p>
          <p><strong>Premium.</strong> Provides access to all Basic features plus the Expert sommelier complexity level, with a limit of fifteen (15) menu scans per calendar month.</p>
          <p>Winesup reserves the right to adjust tier features, scan limits, and pricing at any time in accordance with Section 10. Continued use of a paid tier following a pricing change constitutes acceptance of the revised pricing.</p>

          <p style={{ marginTop: '2rem', fontStyle: 'italic' }}>Last updated: March 14, 2026</p>
        </div>

        <div style={styles.footer}>
          {!reachedBottom && (
            <p style={styles.scrollHint}>Please scroll to the bottom to enable the accept button.</p>
          )}

          <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={checkedTc}
                onChange={(e) => setCheckedTc(e.target.checked)}
                disabled={!reachedBottom}
                style={styles.checkbox}
              />
              I have read and agree to the Terms and Conditions of Winesup
            </label>

            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={checkedAge}
                onChange={(e) => setCheckedAge(e.target.checked)}
                disabled={!reachedBottom}
                style={styles.checkbox}
              />
              I confirm that I am of legal drinking age in my country of residence
            </label>
          </div>

          {error && <p style={styles.error}>{error}</p>}

          <div style={styles.buttonRow}>
            <button
              onClick={handleDecline}
              style={styles.btnDecline}
              disabled={loading}
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              style={canAccept ? styles.btnAccept : styles.btnAcceptDisabled}
              disabled={!canAccept || loading}
            >
              {loading ? 'Processing...' : 'Accept and Continue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    background: '#fff', borderRadius: '12px', maxWidth: '700px', width: '100%',
    maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    padding: '24px 24px 12px', borderBottom: '1px solid #eee',
  },
  title: { margin: '0 0 4px', fontSize: '1.4rem', color: '#1a1a1a' },
  subtitle: { margin: '0 0 12px', color: '#666', fontSize: '0.9rem' },
  progressBar: {
    height: '6px', background: '#eee', borderRadius: '3px', overflow: 'hidden',
  },
  progressFill: {
    height: '100%', background: '#722F37', borderRadius: '3px',
    transition: 'width 0.2s ease',
  },
  progressLabel: { fontSize: '0.8rem', color: '#888', marginTop: '4px', display: 'block' },
  content: {
    padding: '24px', overflowY: 'auto', flex: 1,
    fontSize: '0.9rem', lineHeight: '1.6', color: '#333',
  },
  footer: {
    padding: '16px 24px 24px', borderTop: '1px solid #eee',
  },
  scrollHint: { color: '#e67e22', fontSize: '0.85rem', margin: '0 0 12px' },
  checkboxGroup: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' },
  checkboxLabel: { display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.9rem', cursor: 'pointer' },
  checkbox: { marginTop: '2px', flexShrink: 0 },
  error: { color: '#c0392b', fontSize: '0.85rem', margin: '0 0 12px' },
  buttonRow: { display: 'flex', justifyContent: 'flex-end', gap: '12px' },
  btnDecline: {
    padding: '10px 20px', border: '1px solid #ccc', background: '#fff',
    borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem',
  },
  btnAccept: {
    padding: '10px 24px', background: '#722F37', color: '#fff',
    border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600,
  },
  btnAcceptDisabled: {
    padding: '10px 24px', background: '#ccc', color: '#fff',
    border: 'none', borderRadius: '6px', cursor: 'not-allowed', fontSize: '0.9rem', fontWeight: 600,
  },
};
